import React, { useContext, useEffect, useState, useRef } from 'react'
import { useParams, NavLink } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import PageTitle from '../../../Components/PageTitle'
import { ConfigContext } from '../../../Context/ConfigContext'
import { SubmitBtn } from '../../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../../Components/Shimmer'

const ManageProductGallery = () => {
    const { primaryColor, apiHeaderJson, apiURL, apiHeaderFile } = useContext(ConfigContext)
    const { product_id } = useParams()

    const [galleries, setGalleries] = useState([])
    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [isInfoLoading, setIsInfoLoading] = useState(false)

    // Form state
    const [galleryName, setGalleryName] = useState('')
    const [gallerySeq, setGallerySeq] = useState('') // NEW: sequence
    const [galleryId, setGalleryId] = useState(null)
    const [galleryImage, setGalleryImage] = useState(null) // File object
    const [imagePreview, setImagePreview] = useState('')
    const [errors, setErrors] = useState({})
    const [isDragOver, setIsDragOver] = useState(false)

    const fileInputRef = useRef(null)
    const dropZoneRef = useRef(null)

    // ---------- Image validation ----------
    const validateImageFile = (file) => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
        if (!validTypes.includes(file.type)) {
            return { isValid: false, message: 'Please select a valid image (JPEG, JPG, PNG, WEBP).' }
        }
        const maxSize = 1 * 1024 * 1024 // 1MB
        if (file.size > maxSize) {
            return { isValid: false, message: 'Image size must be less than 1MB.' }
        }
        return { isValid: true }
    }

    const processImageFile = (file) => {
        setGalleryImage(file)
        const preview = URL.createObjectURL(file)
        setImagePreview(preview)
        if (errors.galleryImage) {
            setErrors({ ...errors, galleryImage: '' })
        }
    }

    const handleFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
                if (fileInputRef.current) fileInputRef.current.value = ''
            }
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            const file = files[0]
            const validation = validateImageFile(file)
            if (validation.isValid) {
                processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
            }
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleDropZoneClick = () => {
        fileInputRef.current?.click()
    }

    const removeImage = () => {
        setGalleryImage(null)
        setImagePreview('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // ---------- Data fetching ----------
    const fetchGalleries = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetProductsGallery`, {
                headers: apiHeaderJson,
                params: { product_id }
            })
            const { success, total_records, data } = response.data
            if (success) {
                setGalleries(data || [])
                setGallerySeq(total_records + 1)
            } else {
                Swal.fire('Error!', response.message || 'Failed to load galleries', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong while fetching galleries', 'error')
        } finally {
            setLoading(false)
        }
    }

    const fetchGalleryInfo = async (gallery_id) => {
        try {
            setIsInfoLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetProductGalleryInfo`, {
                headers: apiHeaderJson,
                params: { product_gallery_id: gallery_id }
            })
            const { success, data } = response.data
            if (success && data && data.length > 0) {
                const gallery = data[0]
                setGalleryName(gallery.gallery_name || '')
                setGallerySeq(gallery.gallery_seq ?? '') // NEW: set sequence
                setGalleryId(gallery.product_gallery_id)
                setImagePreview(gallery.product_image_url || '')  // existing image URL
                setGalleryImage(null)  // no new file selected yet
                setErrors({})
            } else {
                Swal.fire('Error!', response.message || 'Failed to load gallery info', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setIsInfoLoading(false)
        }
    }

    const resetForm = () => {
        setGalleryName('')
        setGallerySeq('') // NEW: reset sequence
        setGalleryId(null)
        setGalleryImage(null)
        setImagePreview('')
        setErrors({})
        setIsDragOver(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const validateForm = () => {
        const errors = {}
        // For new entries, image is required
        if (!galleryId && !galleryImage) {
            errors.galleryImage = 'Please select an image'
        }
        // Optional: validate sequence is a number if provided
        if (gallerySeq && isNaN(gallerySeq)) {
            errors.gallerySeq = 'Sequence must be a number'
        }
        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    // ---------- Submit ----------
    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        // If a new image is selected, validate it
        if (galleryImage) {
            const validation = validateImageFile(galleryImage)
            if (!validation.isValid) {
                Swal.fire('Error!', validation.message, 'error')
                return
            }
        }

        setSubmitLoading(true)
        try {
            const formData = new FormData()
            formData.append('product_id', product_id)
            formData.append('gallery_name', galleryName.trim())
            formData.append('gallery_seq', gallerySeq ? Number(gallerySeq) : '') // NEW: include sequence
            if (galleryId) {
                formData.append('product_gallery_id', galleryId)
            }
            if (galleryImage) {
                formData.append('gallery_image', galleryImage)  // adjust field name if needed
            }

            const response = await axios.post(`${apiURL}Masters/CreateProductGallery`, formData, {
                headers: apiHeaderFile  // multipart
            })
            const { message, success } = response.data

            if (success) {
                Swal.fire('Success!', message, 'success')
                resetForm()
                fetchGalleries()
            } else {
                Swal.fire('Error!', message || 'Failed to save gallery', 'error')
            }
        } catch (error) {
            console.error(error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setSubmitLoading(false)
        }
    }

    // ---------- Delete ----------
    const handleDelete = async (gallery_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                setLoading(true)
                const response = await axios.post(`${apiURL}Masters/DeleteProductGallery`,
                    { product_gallery_id: gallery_id },
                    { headers: apiHeaderJson }
                )
                const { message, success } = response.data
                Swal.fire('Deleted!', message, success ? 'success' : 'error')
                if (success) {
                    fetchGalleries()
                }
            } catch (error) {
                console.error(error)
                Swal.fire('Error!', 'Something went wrong', 'error')
            } finally {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        if (product_id) {
            fetchGalleries()
        }
        // Clean up object URLs
        return () => {
            if (imagePreview && imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview)
            }
        }
    }, [product_id])

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <PageTitle title="Manage Product Galleries" primary="Products" />
                    {isInfoLoading && <ContentLoader />}

                    <div className="row">
                        {/* Left column: Form */}
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                    <h4 className="card-title mb-0 text-white">
                                        {galleryId ? 'Update Gallery' : 'Add New Gallery'}
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        {/* Image Upload with DnD */}
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Gallery Image <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                                onChange={handleFileSelect}
                                                className="d-none"
                                                ref={fileInputRef}
                                            />
                                            <div
                                                ref={dropZoneRef}
                                                className={`border-2 border-dashed rounded p-3 text-center cursor-pointer ${isDragOver ? 'border-primary bg-light' : 'border-gray-300'} ${errors.galleryImage ? 'border-danger' : ''}`}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                                onClick={handleDropZoneClick}
                                                style={{ transition: 'all 0.3s ease' }}
                                            >
                                                {imagePreview ? (
                                                    <div className="position-relative">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            className="img-thumbnail mx-auto"
                                                            style={{ maxWidth: '200px', maxHeight: '200px', display: 'block' }}
                                                        />
                                                        {galleryImage && (
                                                            <div className="mt-1 small text-muted">
                                                                Size: {formatFileSize(galleryImage.size)}
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                removeImage()
                                                            }}
                                                            style={{ zIndex: 10 }}
                                                        >
                                                            <i className="ri-close-line" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <i className="ri-upload-cloud-2-line display-4 text-muted mb-2" style={{ fontSize: '2.5rem' }} />
                                                        <p className="mb-1">
                                                            <span className="text-primary">Click to upload</span> or drag and drop
                                                        </p>
                                                        <p className="text-muted small mb-0">
                                                            PNG, JPG, JPEG, WEBP (Max. 1MB)
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            {errors.galleryImage && (
                                                <div className="invalid-feedback d-block">{errors.galleryImage}</div>
                                            )}
                                            <small className="text-muted">
                                                {galleryId ? 'Leave empty to keep current image' : 'Image is required for new galleries'}
                                            </small>
                                        </div>

                                        {/* NEW: Sequence input */}
                                        <div className="mb-3">
                                            <label htmlFor="gallerySeq" className="form-label">
                                                Sequence
                                            </label>
                                            <input
                                                id="gallerySeq"
                                                type="number"
                                                className={`form-control ${errors.gallerySeq ? 'is-invalid' : ''}`}
                                                placeholder="Enter sequence (optional)"
                                                value={gallerySeq}
                                                onChange={(e) => {
                                                    setGallerySeq(e.target.value)
                                                    if (errors.gallerySeq) {
                                                        setErrors({ ...errors, gallerySeq: '' })
                                                    }
                                                }}
                                            />
                                            {errors.gallerySeq && (
                                                <div className="invalid-feedback">{errors.gallerySeq}</div>
                                            )}
                                        </div>

                                        {/* Submit buttons */}
                                        <div className="d-flex gap-2 justify-content-end">
                                            {galleryId && (
                                                <button
                                                    type="button"
                                                    className="btn btn-light"
                                                    onClick={resetForm}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                            <SubmitBtn
                                                disabled={submitLoading}
                                                text={submitLoading ? 'Saving...' : galleryId ? 'Update' : 'Save'}
                                                type="primary"
                                                onClick={handleSubmit}
                                                icon="ri-save-line"
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Right column: List */}
                        <div className="col-md-8">
                            <div className="card">
                                <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                    <h4 className="card-title mb-0 text-white">
                                        Galleries List
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-hover mb-0">
                                            {loading ? (
                                                <TableRows rows="5" colspan="5" />
                                            ) : (
                                                <>
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Seq</th> {/* NEW: Sequence column */}
                                                            <th>Image</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {galleries.length > 0 ? (
                                                            galleries.map((row, index) => (
                                                                <tr key={row.product_gallery_id || row.gallery_id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{row.gallery_seq ?? '-'}</td> {/* NEW: show sequence */}
                                                                    <td>
                                                                        {row.product_image_url ? (
                                                                            <img
                                                                                src={row.product_image_url}
                                                                                alt={row.gallery_name}
                                                                                className="img-thumbnail"
                                                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                                            />
                                                                        ) : (
                                                                            <div className="text-muted">No image</div>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-soft-primary me-2"
                                                                            onClick={() => fetchGalleryInfo(row.product_gallery_id)}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="ri-pencil-line" />
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-soft-danger"
                                                                            onClick={() => handleDelete(row.product_gallery_id)}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="ri-delete-bin-line" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="5" className="text-center"> {/* changed colSpan to 5 */}
                                                                    <NoRecords />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </>
                                            )}
                                        </table>
                                    </div>
                                    <div className="mt-3">
                                        <NavLink to="/Masters/ProductList" className="btn btn-sm btn-secondary">
                                            <i className="ri-arrow-left-line me-1" /> Back to Products
                                        </NavLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ManageProductGallery